"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileSearch, Wrench, Globe as Globe2, Star, Code as Code2, ChartLine as LineChart, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const steps = [{
  stepNumber: "01",
  title: "Comprehensive SEO Audit",
  shortTitle: "Audit",
  description: "I perform a deep-dive analysis into your website's technical health, content gaps, indexation, and competitive positioning to identify high-impact growth blockers.",
  icon: FileSearch,
  deliverables: ["Technical crawler & indexation review", "Keyword & search intent mapping", "Competitor gap & bottleneck analysis"]
}, {
  stepNumber: "02",
  title: "Strategic Optimization",
  shortTitle: "Optimization",
  description: "I personally overhaul your on-page structure, metadata, site architecture, and content to align directly with user demand and search engine algorithms.",
  icon: Wrench,
  deliverables: ["High-intent content optimization", "Information architecture refinement", "Core Web Vitals & technical fixes"]
}, {
  stepNumber: "03",
  title: "Citations & Authority",
  shortTitle: "Citations",
  description: "I build a strong digital footprint by securing authoritative local and industry-specific citations, establishing undeniable geographic and topical relevance.",
  icon: Globe2,
  deliverables: ["High-authority business citations", "Google Business Profile harmonization", "Clean NAP (Name, Address, Phone) consistency"]
}, {
  stepNumber: "04",
  title: "Reputation & Reviews",
  shortTitle: "Reviews",
  description: "I help design structured customer review generation flows to strengthen trust signals for both search engines and potential buyers looking for your services.",
  icon: Star,
  deliverables: ["Review collection strategy setup", "Trust signal integration", "Customer feedback optimization"]
}, {
  stepNumber: "05",
  title: "Schema & Entity Building",
  shortTitle: "Schema",
  description: "I implement precise structured data markup so Google and AI search engines like Perplexity or ChatGPT accurately understand your business entities.",
  icon: Code2,
  deliverables: ["Custom JSON-LD schema implementation", "Entity mapping for modern AI search", "Rich snippet enhancement"]
}, {
  stepNumber: "06",
  title: "Reporting & Refinement",
  shortTitle: "Reporting",
  description: "You receive clear, jargon-free monthly performance reports directly from me, tracking organic rankings, conversions, and tangible business revenue growth.",
  icon: LineChart,
  deliverables: ["Customized executive dashboard", "Conversion & pipeline tracking", "Ongoing strategic refinements"]
}];
export default function Methodology() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="methodologyprocessflow" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground overflow-hidden">
      <div className="absolute top-1/4 left-0 w-72 h-72 bg-secondary/40 rounded-full blur-3xl pointer-events-none -z-10" />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none -z-10" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs sm:text-sm font-medium bg-secondary text-secondary-foreground border border-accent/20">
            <Sparkles className="w-3.5 h-3.5 text-accent" />
            Transparent Methodology
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground mb-6">
            How You Work Directly With Me
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            No account managers or junior trainees. A structured, 6-step hands-on SEO process designed to deliver sustainable search visibility and high-converting audience growth.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 relative">
          {steps.map((step, i) => {
          const IconComponent = step.icon;
          return <motion.div key={step.stepNumber} data-index={i} variants={itemVariants} className="flex">
                <Card className="flex flex-col justify-between w-full border border-border/80 bg-card hover:border-primary/40 transition-[border-color,box-shadow] duration-300 shadow-sm hover:shadow-md relative overflow-hidden group">
                  <div className="h-1.5 w-full bg-gradient-to-r from-primary via-accent to-secondary" />

                  <CardHeader className="pt-6 pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary text-primary-foreground font-bold font-mono text-sm shadow-xs">
                        {step.stepNumber}
                      </span>

                      <div className="p-2.5 rounded-lg bg-secondary/60 text-primary group-hover:bg-accent group-hover:text-accent-foreground transition-[background-color,color] duration-300">
                        <IconComponent className="w-5 h-5" />
                      </div>
                    </div>

                    <CardTitle className="text-xl font-semibold font-default text-foreground group-hover:text-primary transition-colors duration-200">
                      {step.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed mt-2">
                      {step.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0 pb-6 flex-grow flex flex-col justify-end">
                    <div className="pt-4 border-t border-border/60">
                      <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">
                        Key Deliverables
                      </p>
                      <ul className="space-y-2">
                        {step.deliverables.map((item, itemIdx) => <li key={itemIdx} className="flex items-start text-xs sm:text-sm text-muted-foreground gap-2">
                            <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-12 p-6 rounded-2xl bg-muted/50 border border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Sparkles className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold font-default text-foreground">
                Tailored Specifically To Your Market
              </h3>
              <p className="text-sm text-muted-foreground">
                Whether national, international, or local SEO, every step is customized to your exact objectives.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0 w-full md:w-auto">
            <Button asChild size="lg" className="w-full md:w-auto font-medium">
              <Link to="/ContactPage">
                <span>Book a Free Consultation</span>
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}